import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createClient } from "@supabase/supabase-js";
import "./AdminContactInbox.css";

const supabaseUrl =
  process.env.REACT_APP_SUPABASE_URL ||
  "https://zvacumxidqqaueuruwnj.supabase.co";

const supabaseAnonKey =
  process.env.REACT_APP_SUPABASE_ANON_KEY ||
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inp2YWN1bXhpZHFxYXVldXJ1d25qIiwicm9sZSI6ImFub24iLCJpYXQiOjE3Nzg1NTk4ODQsImV4cCI6MjA5NDEzNTg4NH0.PkuVH1cMeGpG5GTckXBNuSdvWFwUij27s25M1MkKYgQ";

const supabase = createClient(supabaseUrl, supabaseAnonKey);

type ContactKind = "general" | "issue" | "survey";
type ContactStatus = "new" | "saved";

type ContactItem = {
  id: string;
  kind: ContactKind;
  status: ContactStatus;
  created_at: string;

  school_name?: string | null;
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  body?: string | null;
  created_by?: string | null;

  survey_json?: any | null;

  file_url?: string | null;
  file_name?: string | null;
};

function clean(v: any) {
  return String(v ?? "").trim();
}

function fmtDT(iso: string) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso || "—";
  return d.toLocaleString();
}

function badgeClass(kind: ContactKind) {
  if (kind === "general") return "aci-badge general";
  if (kind === "issue") return "aci-badge issue";
  return "aci-badge survey";
}

function normalizeKind(v: any): ContactKind {
  const x = clean(v).toLowerCase();
  if (x === "issue" || x === "bug" || x === "problem" || x === "issue_report") return "issue";
  if (x === "survey" || x === "feedback" || x === "rating") return "survey";
  return "general";
}

function normalizeStatus(v: any): ContactStatus {
  const x = clean(v).toLowerCase();
  if (x === "handled" || x === "saved" || x === "closed" || x === "resolved") return "saved";
  return "new";
}

function toErrorText(detail: any, fallback = "Request failed"): string {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;

  if (Array.isArray(detail)) {
    return detail
      .map((item: any) => {
        const where = Array.isArray(item?.loc) ? item.loc.join(" > ") : "";
        const msg =
          typeof item?.msg === "string"
            ? item.msg
            : typeof item === "string"
              ? item
              : "Validation error";
        return where ? `${where}: ${msg}` : msg;
      })
      .join(" | ");
  }

  if (typeof detail === "object") {
    if (typeof detail?.detail === "string") return detail.detail;
    if (typeof detail?.message === "string") return detail.message;

    try {
      return JSON.stringify(detail);
    } catch {
      return fallback;
    }
  }

  return String(detail);
}

function normalizeListPayload(data: any): ContactItem[] {
  const arr =
    (Array.isArray(data) && data) ||
    (Array.isArray(data?.items) && data.items) ||
    (Array.isArray(data?.results) && data.results) ||
    (Array.isArray(data?.contacts) && data.contacts) ||
    (Array.isArray(data?.rows) && data.rows) ||
    [];

  return (arr as any[])
    .map((x) => {
      const surveyJson =
        x?.survey_json ??
        x?.survey ??
        x?.answers ??
        x?.metadata?.survey ??
        null;

      const schoolName =
        x?.school_name ??
        x?.schools?.school_name ??
        x?.school?.school_name ??
        x?.metadata?.school_name ??
        null;

      return {
        id: clean(x?.id ?? x?.contact_id),
        kind: normalizeKind(x?.kind ?? x?.type ?? x?.contact_type ?? x?.category),
        status: normalizeStatus(x?.status ?? x?.contact_status),
        created_at: clean(x?.created_at ?? x?.created ?? x?.created_on ?? x?.submitted_at),

        school_name: clean(schoolName) || null,
        name: clean(x?.name ?? x?.full_name ?? x?.contact_name ?? x?.created_by) || null,
        email: clean(x?.email ?? x?.contact_email) || null,
        phone: clean(x?.phone ?? x?.contact_phone) || null,
        created_by: clean(x?.created_by) || null,

        body:
          clean(
            x?.body ??
              x?.message ??
              x?.notes ??
              x?.content ??
              x?.contact_message ??
              x?.subject
          ) || null,

        survey_json: surveyJson,

        file_url:
          clean(x?.file_url ?? x?.attachment_url ?? x?.upload_url) || null,

        file_name:
          clean(x?.file_name ?? x?.attachment_name ?? x?.upload_name) || null,
      } as ContactItem;
    })
    .filter((x) => !!x.id);
}

const SURVEY_QUESTIONS: Record<string, string> = {
  q1: "Ease of login and navigation",
  q2: "Design and look/feel",
  q3: "Speed/performance",
  q4: "Helpfulness of wellness information",
  q5: "Safety/trust/comfort using the app",
  q6: "Overall satisfaction",
};

function parseSurveyPairs(
  survey: any,
  body?: string | null
): Array<{ key: string; label: string; rating: number }> {
  let source = survey;

  if (!source && body) {
    const parsed: Record<string, number> = {};

    String(body)
      .split("\n")
      .forEach((line) => {
        const match = line.trim().match(/^(q[1-6])\s*:\s*([1-5])/i);
        if (match) {
          parsed[match[1].toLowerCase()] = Number(match[2]);
        }
      });

    source = Object.keys(parsed).length ? parsed : null;
  }

  if (!source) return [];

  const entries: Array<[string, any]> = Array.isArray(source)
    ? source
        .map((x: any) => [String(x?.key ?? x?.id ?? ""), x?.value ?? x?.rating] as [string, any])
        .filter(([k]) => !!k)
    : Object.entries(source);

  const pairs = entries
    .map(([k, v]) => {
      const key = String(k || "").trim().toLowerCase();
      const n = Number(v);
      const rating = Number.isFinite(n) ? Math.max(0, Math.min(5, Math.round(n))) : 0;
      const label = SURVEY_QUESTIONS[key] || key;
      return { key, label, rating };
    })
    .filter((x) => x.key && x.key.startsWith("q"));

  pairs.sort((a, b) => {
    const na = Number(a.key.replace(/[^\d]/g, "")) || 0;
    const nb = Number(b.key.replace(/[^\d]/g, "")) || 0;
    return na - nb;
  });

  return pairs;
}

function avgRating(pairs: Array<{ rating: number }>) {
  if (!pairs.length) return 0;
  const sum = pairs.reduce((acc, p) => acc + (Number(p.rating) || 0), 0);
  return Math.round((sum / pairs.length) * 10) / 10;
}

export default function AdminContactInbox() {
  const [items, setItems] = useState<ContactItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [openIds, setOpenIds] = useState<string[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");

    try {
      const { data, error } = await supabase
        .from("contacts")
        .select(
          `
          contact_id,
          school_id,
          contact_type,
          message,
          created_by,
          created_at,
          schools (
            school_name
          )
        `
        )
        .neq("message", "Student requested counselor follow-up.")
        .order("created_at", { ascending: false });

      if (error) throw error;

      const next = normalizeListPayload(data || []);
      setItems(next);
      setOpenIds((prev) => prev.filter((id) => next.some((x) => x.id === id)));
    } catch (e: any) {
      setItems([]);
      setErr(toErrorText(e?.message || e, "Failed to load contact inbox from Supabase."));
      console.error("[ADMIN CONTACT INBOX SUPABASE LOAD]", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function remove(id: string) {
    if (!window.confirm("Delete this contact item? This cannot be undone.")) return;

    try {
      const { error } = await supabase
        .from("contacts")
        .delete()
        .eq("contact_id", id);

      if (error) throw error;

      setOpenIds((prev) => prev.filter((x) => x !== id));
      await load();
    } catch (e: any) {
      alert(toErrorText(e?.message || e, "Failed to delete contact item."));
    }
  }

  function toggleView(id: string) {
    setOpenIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }

  const generalItems = useMemo(() => items.filter((x) => x.kind === "general"), [items]);
  const issueItems = useMemo(() => items.filter((x) => x.kind === "issue"), [items]);
  const surveyItems = useMemo(() => items.filter((x) => x.kind === "survey"), [items]);

  function renderExpanded(item: ContactItem) {
    if (item.kind === "general") {
      return (
        <div className="aci-expanded">
          <div className="aci-detailGrid">
            <div><b>School:</b> {clean(item.school_name) || "—"}</div>
            <div><b>Name:</b> {clean(item.name) || "—"}</div>
            <div><b>Email:</b> {clean(item.email) || "—"}</div>
            <div><b>Phone:</b> {clean(item.phone) || "—"}</div>
            <div><b>Created by:</b> {clean(item.created_by) || "—"}</div>
          </div>
          <div className="aci-body">{clean(item.body) || "—"}</div>
        </div>
      );
    }

    if (item.kind === "issue") {
      return (
        <div className="aci-expanded">
          <div className="aci-detailGrid">
            <div><b>School:</b> {clean(item.school_name) || "—"}</div>
            <div><b>Created by:</b> {clean(item.created_by) || "—"}</div>
          </div>

          <div className="aci-body">{clean(item.body) || "—"}</div>

          {item.file_url ? (
            <div className="aci-attach">
              <div className="aci-attachTitle">Attachment</div>
              <a href={item.file_url} target="_blank" rel="noreferrer">
                Open screenshot
              </a>
              {item.file_name ? <div className="aci-muted">{item.file_name}</div> : null}
            </div>
          ) : (
            <div className="aci-muted">No screenshot attached.</div>
          )}
        </div>
      );
    }

    const pairs = parseSurveyPairs(item.survey_json, item.body);
    const avg = avgRating(pairs);

    return (
      <div className="aci-expanded">
        <div className="aci-detailGrid">
          <div><b>School:</b> {clean(item.school_name) || "—"}</div>
          <div><b>Name:</b> {clean(item.name) || "—"}</div>
          <div><b>Email:</b> {clean(item.email) || "—"}</div>
        </div>

        {pairs.length ? (
          <div className="survey-pretty">
            <div className="survey-summary">
              <div className="survey-summary-title">Overall</div>
              <div>
                <span className="stars">
                  {"★".repeat(Math.round(avg))}
                  {"☆".repeat(5 - Math.round(avg))}
                </span>
                <span className="survey-score">{avg}/5</span>
              </div>
            </div>

            {pairs.map((p) => (
              <div key={p.key} className="survey-row">
                <div className="survey-question">{p.label}</div>
                <div>
                  <span className="stars">
                    {"★".repeat(p.rating)}
                    {"☆".repeat(5 - p.rating)}
                  </span>
                  <span className="survey-score">({p.rating}/5)</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="aci-muted">No survey answers found.</div>
        )}

        {item.body && !pairs.length ? <div className="aci-body">{clean(item.body)}</div> : null}
      </div>
    );
  }

  function renderCard(item: ContactItem) {
    const isOpen = openIds.includes(item.id);

    const title =
      item.kind === "issue"
        ? "App Issue Report"
        : item.kind === "survey"
          ? clean(item.name) || "User Survey"
          : clean(item.school_name) || clean(item.name) || "General Message";

    const snippet =
      item.kind === "survey"
        ? "Survey response submitted."
        : clean(item.body) || "No message provided.";

    return (
      <article key={item.id} className="aci-card">
        <div className="aci-cardTop">
          <div className="aci-cardMain">
            <div className="aci-cardMeta">
              <span className={badgeClass(item.kind)}>{item.kind.toUpperCase()}</span>
              <span className={`aci-status ${item.status}`}>{item.status.toUpperCase()}</span>
            </div>

            <div className="aci-cardTitle">{title}</div>

            <div className="aci-cardSub">
              <span>{fmtDT(item.created_at)}</span>
              {item.school_name ? <span className="aci-dot">•</span> : null}
              {item.school_name ? <span>{item.school_name}</span> : null}
              {item.file_name ? <span className="aci-dot">•</span> : null}
              {item.file_name ? <span>📎 {item.file_name}</span> : null}
            </div>
          </div>
        </div>

        <div className="aci-snippet">
          {snippet.length > 160 && !isOpen ? `${snippet.slice(0, 160)}...` : snippet}
        </div>

        <div className="aci-actions">
          <button className="aci-btn" onClick={() => toggleView(item.id)} type="button">
            {isOpen ? "Hide" : "View"}
          </button>
          <button className="aci-btn danger" onClick={() => remove(item.id)} type="button">
            Delete
          </button>
        </div>

        {isOpen ? renderExpanded(item) : null}
      </article>
    );
  }

  function renderColumn(title: string, kindName: ContactKind, colItems: ContactItem[]) {
    return (
      <section className="aci-column" key={kindName}>
        <div className="aci-columnHead">
          <div className="aci-columnTitleWrap">
            <h3 className="aci-columnTitle">{title}</h3>
            <span className="aci-columnCount">{colItems.length}</span>
          </div>
        </div>

        <div className="aci-columnBody">
          {colItems.length ? (
            colItems.map((item) => renderCard(item))
          ) : (
            <div className="aci-empty">No {title.toLowerCase()} items.</div>
          )}
        </div>
      </section>
    );
  }

  return (
    <div className="aci-page">
      <div className="aci-header">
        <div className="aci-title">CONTACT INBOX</div>

        <div className="aci-filters">
          <button className="aci-btn" onClick={load} disabled={loading} type="button">
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {err ? <div className="aci-alert error">{err}</div> : null}

      <div className="aci-board">
        {renderColumn("General", "general", generalItems)}
        {renderColumn("Issue Reports", "issue", issueItems)}
        {renderColumn("Survey", "survey", surveyItems)}
      </div>
    </div>
  );
}